/**
 * b18k: ScrollToTop is now a thin wrapper around useScrollResetOnRoute.
 * The scroll-reset logic lives in the hook so it can be tested and reused
 * independently of a rendered component tree.
 *
 * Previous implementation (pre-b18k) inlined the resetAllScrollOwners logic
 * here. That logic is now owned by useScrollResetOnRoute.
 *
 * b18f item 3 notes (preserved for context):
 *   - A single synchronous window.scrollTo(0,0) was unreliable on mobile
 *     WebViews because Capacitor restores the prior scroll offset AFTER our
 *     reset fires (post-layout). The double-rAF in useScrollResetOnRoute
 *     overwrites that late restore.
 *   - All scroll owners are reset: window, document.scrollingElement,
 *     documentElement, body, and any [data-scroll-root] containers.
 */
import { useScrollResetOnRoute } from "@/hooks/useScrollResetOnRoute";
import { usePageReturnRestore } from "@/hooks/usePageReturnRestore";

export function ScrollToTop() {
  // The dex/Pokédex variant-swipe surfaces (/dex, /pokedex and dex sub-routes
  // such as /dex/:variant/:dexNumber/forms) intentionally preserve scroll as
  // the user swipes between dex variants. Exempt them so sub-navigation that
  // stays within the dex region keeps its offset, while entering the dex from
  // any other tab still resets to the top (see useScrollResetOnRoute).
  // preserveReturnFromPrefixes: returning from a /pokemon/:id detail page to the
  // Dex must NOT reset — the Dex restores the exact scroll row the trainer tapped
  // from (dexScrollRestore). Entering a detail page still resets (it opens at the
  // top), and every other tab change resets as before.
  // returnPairs: My Pokémon's record page is NESTED under the list route, so the
  // prefix options above cannot separate the three moves that share it. Only
  // /my-pokemon/:id → /my-pokemon is exempt (the list restores the row the
  // trainer was on, via myPokemonScrollRestore); opening a record, and swiping
  // from one record to the next, still land at the top.
  // "/pokemon/" is in the skip region so the central reset stands down when a
  // species page is entered from the Dex or from another species page (a
  // sideways swipe). Those two are the cases where the species page scrolls
  // ITSELF to the top in a layout effect, pre-paint — which is what the central
  // reset could not do from a passive effect, and what left iOS WKWebView
  // painting the new species at the old offset and then scrolling it, with the
  // tap targets stale on the old layer until you nudged the page. Every other
  // route into a species still resets centrally (the layout effect makes that a
  // no-op), and /pokemon/:id → /dex is untouched: it stays suppressed by
  // preserveReturnFromPrefixes so the Dex can restore its own row.
  useScrollResetOnRoute({
    skipPrefixes: ["/dex", "/pokedex", "/pokemon/"],
    preserveReturnFromPrefixes: ["/pokemon"],
    returnPairs: [
      { to: "/my-pokemon", from: "/my-pokemon/" },
      // A record opened from the Battle Hub's compare boxes returns to the
      // hub, which restores its own offset (battleHubMemory).
      { to: "/battle", from: "/my-pokemon/" },
    ],
  });
  // The counterpart of the reset: a Back navigation armed with an offset
  // (pageReturn) is restored here. Declared AFTER the reset hook on purpose:
  // effects run in declaration order, the reset's effect reads the armed
  // flag and stands down, and only then does this one consume it.
  usePageReturnRestore();
  return null;
}
